import {
  HOME_TANK_LEVEL_LABELS,
  type HomeTankLevel,
  REPORT_SALES_UNIT_LABELS,
  type ReportsHome,
} from "@station/contracts";
import {
  Alert,
  Card,
  EmptyState,
  MoneyText,
  PageHeader,
  QuantityText,
  Skeleton,
  StatusBadge,
} from "@station/ui";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowRight,
  CheckCircle2,
  ClipboardCheck,
  Droplets,
  FileBadge,
  Router,
  Store,
  TrendingUp,
  TriangleAlert,
  Wrench,
} from "lucide-react";
import type { ReactElement, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { HomeAlarmCard } from "../../features/alarms/home-alarm-card.js";
import { formatPercentChange } from "../../features/alarms/alarm-values.js";
import { useAlarmPermissions } from "../../features/alarms/queries.js";
import { SeverityBadge } from "../../features/alarms/shared.js";
import { ChartWithTable } from "../../features/fuel/shared.js";
import { useLiveToday, useReportPermissions, useReportsHome } from "../../features/reports/queries.js";
import { changeTone, formatCompactMoney, trendChartOption } from "../../features/reports/report-values.js";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api.js";
import { errorMessage } from "../../lib/errors.js";
import { formatDate, formatDateTime } from "../../lib/format.js";
import { useDocumentTitle } from "../../lib/hooks.js";
import { queryKeys } from "../../lib/query.js";
import { useScope } from "../../lib/scope.js";
import { useMe, usePermissions } from "../../lib/session.js";
import { useTenantShape } from "../../lib/tenant-shape.js";
import { useMenuContext, useWorkspace } from "../../lib/use-workspace.js";
import type { QuickAction } from "../../lib/workspaces.js";

export const Route = createFileRoute("/_app/")({ component: HomePage });

/** Sunucu en çok 10 kapanmamış gün / iş emri / belge döndürür. */
const HOME_LIST_LIMIT = 10;
/** Bekleyen iş satırında ayrıntı olarak gösterilen kayıt sayısı. */
const DETAIL_PREVIEW = 2;

const TANK_TONES: Record<HomeTankLevel, "danger" | "warning" | "info"> = {
  CRITICAL: "danger",
  LOW: "warning",
  RUNNING_OUT: "info",
};

/** İstanbul saatine göre selamlama. */
function greetingKey(now: Date): "morning" | "afternoon" | "evening" {
  const hour = Number(
    new Intl.DateTimeFormat("en-GB", {
      hour: "numeric",
      hourCycle: "h23",
      timeZone: "Europe/Istanbul",
    }).format(now),
  );
  if (hour >= 5 && hour < 12) return "morning";
  if (hour >= 12 && hour < 18) return "afternoon";
  return "evening";
}

/**
 * Ana sayfa — GÖREV ODAKLI (ADR 0027; ADR 0022 §7'nin istisna odaklı düzenini sadeleştirir): kim olduğu ve nerede çalıştığı,
 * "Ne yapmak istiyorsunuz?" (rolünün en sık işleri, doğrudan forma götüren düğmeler), "Bekleyen işler" (karar, gün sonu,
 * tank, alarm, iş emri — her satırın tek bir eylemi var), ardından yöneticiler için işletmenin genel durumu (bugün, son 14
 * gün). Her bölüm sunucuda kaynağının izniyle süzülür; üst çubukta tek istasyon seçiliyse listeler o istasyona indirgenir.
 */
function HomePage(): ReactElement {
  const { t } = useTranslation();
  const me = useMe().data;
  const shape = useTenantShape();
  const scope = useScope();
  const reports = useReportPermissions();
  const alarms = useAlarmPermissions();
  const workspace = useWorkspace(useMenuContext());
  useDocumentTitle(t("menu.home"));

  const stations = useQuery({
    queryKey: queryKeys.stations({ home: true }),
    enabled: me?.activeTenant !== null,
    queryFn: async () => (await api.GET("/stations", { params: { query: { limit: 200 } } })).data!.items,
  });
  const station = shape.isSingleStation
    ? stations.data?.[0]
    : scope.kind === "station"
      ? stations.data?.find((item) => item.id === scope.stationId)
      : undefined;
  const scopedStationId = !shape.isSingleStation && scope.kind === "station" ? scope.stationId : null;

  const firstName = me?.user.fullName.split(/\s+/)[0] ?? "";
  const roles = [...new Set(me?.activeTenant?.assignments.map((assignment) => assignment.roleName) ?? [])];
  const place = station
    ? [station.name, station.code].filter(Boolean).join(" · ")
    : t("home.overviewDescription", { count: stations.data?.length ?? me?.activeTenant?.stationCount ?? 0 });
  const today = new Intl.DateTimeFormat("tr-TR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    timeZone: "Europe/Istanbul",
  }).format(new Date());

  return (
    <>
      <PageHeader
        title={t(`work.home.greeting.${greetingKey(new Date())}`, { name: firstName })}
        description={[roles.join(", "), place].filter(Boolean).join(" · ")}
        actions={<span className="text-sm text-muted-foreground first-letter:uppercase">{today}</span>}
      />
      {stations.data?.length === 0 ? (
        <EmptyState title={t("home.noStations.title")} description={t("home.noStations.description")} />
      ) : (
        <div className="grid gap-8">
          <QuickActions
            actions={workspace.quickActions}
            more={workspace.createActions.some(
              (action) => !workspace.quickActions.some((shown) => shown.key === action.key),
            )}
          />
          {reports.canRead ? (
            <TaskHome
              stationId={scopedStationId}
              multiStation={!shape.isSingleStation}
              overview={workspace.overview}
            />
          ) : alarms.enabled && alarms.canRead ? (
            <section aria-labelledby="home-tasks-title" className="grid gap-3">
              <h2 id="home-tasks-title" className="text-lg font-semibold">
                {t("work.home.tasksTitle")}
              </h2>
              <div className="max-w-2xl">
                <HomeAlarmCard stationId={scopedStationId} />
              </div>
            </section>
          ) : null}
        </div>
      )}
    </>
  );
}

// ── Hızlı işlemler ────────────────────────────────────────────────────────────
function QuickActions({
  actions,
  more,
}: {
  actions: readonly QuickAction[];
  more: boolean;
}): ReactElement | null {
  const { t } = useTranslation();
  if (actions.length === 0) return null;
  return (
    <section aria-labelledby="home-actions-title" className="grid gap-3">
      <h2 id="home-actions-title" className="text-lg font-semibold">
        {t("work.home.actionsTitle")}
      </h2>
      <ul className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {actions.map((action) => (
          <li key={action.key} className="flex">
            <Link
              to={action.to}
              search={action.search as never}
              className="group flex w-full items-start gap-3 rounded-xl border bg-card p-4 shadow-card transition hover:-translate-y-0.5 hover:border-primary/50 hover:shadow-md"
            >
              <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary transition group-hover:bg-primary group-hover:text-primary-foreground">
                <action.icon className="size-5" aria-hidden="true" />
              </span>
              <span className="min-w-0">
                <span className="block font-semibold leading-tight">
                  {t(`work.actions.${action.key}.title`)}
                </span>
                <span className="mt-1 block text-sm leading-snug text-muted-foreground">
                  {t(`work.actions.${action.key}.description`)}
                </span>
              </span>
            </Link>
          </li>
        ))}
      </ul>
      {more ? <p className="text-xs text-muted-foreground">{t("work.home.actionsMore")}</p> : null}
    </section>
  );
}

// ── Bekleyen işler + genel durum ──────────────────────────────────────────────
function TaskHome({
  stationId,
  multiStation,
  overview,
}: {
  stationId: string | null;
  multiStation: boolean;
  overview: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const home = useReportsHome();
  const live = useLiveToday(multiStation && overview);
  if (home.error) return <Alert tone="danger">{errorMessage(home.error)}</Alert>;
  if (!home.data)
    return (
      <div className="grid gap-4" role="status" aria-label={t("common.loading")}>
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  const data = home.data;
  return (
    <div className="grid gap-8">
      <PendingTasks issues={data.issues} stationId={stationId} asOf={data.asOf} />
      {overview && data.today ? (
        <Today today={data.today} live={multiStation ? live.data : undefined} stationId={stationId} />
      ) : null}
      {overview && data.trend ? <Trend trend={data.trend} /> : null}
    </div>
  );
}

interface TaskRow {
  key: string;
  tone: "danger" | "warning" | "info";
  icon: ReactNode;
  title: string;
  detail?: ReactNode;
  action: { label: string; to: string; search?: Record<string, string> };
}

const TONE_DOT: Record<TaskRow["tone"], string> = {
  danger: "bg-destructive",
  warning: "bg-warning",
  info: "bg-info",
};

const detailLink = "hover:underline underline-offset-2";
const linkClass = "font-medium text-primary underline-offset-4 hover:underline";

/**
 * Bekleyen işler: her satır TEK bir durum ve TEK bir eylem ("Günü kapat", "Onayla", "Sipariş ver"). Kayıt düzeyindeki
 * ayrıntı (ilk iki iş emri, belge, alarm) satırın altında bağlantı olarak durur; tamamı ilgili sayfadadır.
 */
function PendingTasks({
  issues,
  stationId,
  asOf,
}: {
  issues: ReportsHome["issues"];
  stationId: string | null;
  asOf: string;
}): ReactElement {
  const { t } = useTranslation();
  const canOrderFuel = usePermissions().has("fuel.order.manage");
  const inScope = <T extends { stationId: string | null }>(items: readonly T[] | null) =>
    items ? items.filter((item) => !stationId || item.stationId === stationId) : [];
  const tanks = inScope(issues.criticalTanks);
  const agents = inScope(issues.offlineAgents);
  const days = inScope(issues.unclosedDays);
  const workOrders = inScope(issues.slaBreachedWorkOrders);
  const documents = (issues.expiringDocuments ?? []).filter(
    (item) => !stationId || item.stationId === stationId || item.stationId === null,
  );
  const alarmTop = issues.alarms ? inScope(issues.alarms.top) : [];
  const alarmCount = issues.alarms ? issues.alarms.critical + issues.alarms.important : 0;

  const rows: TaskRow[] = [];
  if (issues.alarms && alarmCount > 0)
    rows.push({
      key: "alarms",
      tone: issues.alarms.critical > 0 ? "danger" : "warning",
      icon: <TriangleAlert />,
      title: t("work.home.tasks.alarms.title", { count: alarmCount }),
      detail: (
        <>
          <span>
            {t("work.home.tasks.alarms.detail", {
              critical: issues.alarms.critical,
              important: issues.alarms.important,
            })}
          </span>
          {alarmTop.slice(0, DETAIL_PREVIEW).map((alarm) => (
            <span key={alarm.id} className="flex min-w-0 items-center gap-1.5">
              <SeverityBadge severity={alarm.severity} />
              <Link
                to="/alarmlar/$alarmId"
                params={{ alarmId: alarm.id }}
                className={`truncate ${detailLink}`}
              >
                {alarm.title}
              </Link>
            </span>
          ))}
        </>
      ),
      action: { label: t("work.home.tasks.alarms.action"), to: "/alarmlar" },
    });
  if ((issues.pendingApprovals ?? 0) > 0)
    rows.push({
      key: "approvals",
      tone: "warning",
      icon: <ClipboardCheck />,
      title: t("work.home.tasks.approvals.title", { count: issues.pendingApprovals }),
      action: { label: t("work.home.tasks.approvals.action"), to: "/onaylar" },
    });
  if (days.length > 0) {
    const oldest = days[0]!;
    rows.push({
      key: "days",
      tone: "warning",
      icon: <Store />,
      title:
        days.length >= HOME_LIST_LIMIT
          ? t("work.home.tasks.days.titleAtLeast", { count: days.length })
          : t("work.home.tasks.days.title", { count: days.length }),
      detail: t("work.home.tasks.days.detail", {
        station: oldest.stationCode,
        date: formatDate(oldest.businessDate),
      }),
      action: { label: t("work.home.tasks.days.action"), to: "/vardiya/gun-sonu" },
    });
  }
  if (tanks.length > 0)
    rows.push({
      key: "tanks",
      tone: tanks.some((tank) => tank.level === "CRITICAL") ? "danger" : "warning",
      icon: <Droplets />,
      title: t("work.home.tasks.tanks.title", { count: tanks.length }),
      detail: tanks.slice(0, DETAIL_PREVIEW).map((tank) => (
        <span key={tank.tankId} className="flex min-w-0 flex-wrap items-center gap-1.5">
          <StatusBadge tone={TANK_TONES[tank.level]}>{HOME_TANK_LEVEL_LABELS[tank.level]}</StatusBadge>
          <span className="font-medium text-foreground">{tank.label}</span>
          <span>
            {tank.productName}
            {tank.fillPercent !== null ? ` · %${tank.fillPercent}` : ""}
            {tank.estimatedRunOutAt
              ? ` · ${t("home.issues.runOut", { at: formatDateTime(tank.estimatedRunOutAt) })}`
              : ""}
          </span>
        </span>
      )),
      action: canOrderFuel
        ? { label: t("work.home.tasks.tanks.action"), to: "/akaryakit/siparisler/yeni" }
        : { label: t("work.home.tasks.tanks.actionView"), to: "/akaryakit/tanklar" },
    });
  if (workOrders.length > 0)
    rows.push({
      key: "workOrders",
      tone: workOrders.some((order) => order.priority === "URGENT") ? "danger" : "warning",
      icon: <Wrench />,
      title: t("work.home.tasks.workOrders.title", { count: workOrders.length }),
      detail: workOrders.slice(0, DETAIL_PREVIEW).map((order) => (
        <Link
          key={order.workOrderId}
          to="/bakim/is-emirleri/$id"
          params={{ id: order.workOrderId }}
          className={`truncate ${detailLink}`}
        >
          {order.stationCode} · {order.title}
        </Link>
      )),
      action: {
        label: t("work.home.tasks.workOrders.action"),
        to: "/bakim/is-emirleri",
        search: { sla: "1" },
      },
    });
  if (documents.length > 0)
    rows.push({
      key: "documents",
      tone: documents.some((document) => document.daysLeft < 0) ? "danger" : "warning",
      icon: <FileBadge />,
      title: t("work.home.tasks.documents.title", { count: documents.length }),
      detail: documents.slice(0, DETAIL_PREVIEW).map((document) => (
        <Link
          key={document.documentId}
          to="/uyum/belgeler/$id"
          params={{ id: document.documentId }}
          className={`truncate ${detailLink}`}
        >
          {document.stationCode} · {document.label} ·{" "}
          {document.daysLeft < 0
            ? t("home.issues.expiredDays", { count: -document.daysLeft })
            : t("home.issues.expiresInDays", { count: document.daysLeft })}
        </Link>
      )),
      action: { label: t("work.home.tasks.documents.action"), to: "/uyum/belgeler" },
    });
  if (agents.length > 0)
    rows.push({
      key: "agents",
      tone: "danger",
      icon: <Router />,
      title: t("work.home.tasks.agents.title", { count: agents.length }),
      detail: agents
        .slice(0, DETAIL_PREVIEW)
        .map((agent) =>
          [
            `${agent.stationCode} · ${agent.stationName}`,
            agent.lastSeenAt
              ? t("home.issues.lastSeen", { at: formatDateTime(agent.lastSeenAt) })
              : t("home.issues.neverSeen"),
          ].join(" — "),
        )
        .join(" · "),
      action: { label: t("work.home.tasks.agents.action"), to: "/ajanlar" },
    });

  return (
    <section aria-labelledby="home-tasks-title" className="grid gap-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 id="home-tasks-title" className="flex items-center gap-2 text-lg font-semibold">
          {t("work.home.tasksTitle")}
          {rows.length > 0 ? <StatusBadge tone="warning">{rows.length}</StatusBadge> : null}
        </h2>
        <span className="text-xs text-muted-foreground">{t("home.asOf", { at: formatDateTime(asOf) })}</span>
      </div>
      {rows.length === 0 ? (
        <Card className="flex items-center gap-3 p-4">
          <CheckCircle2 className="size-5 text-success" aria-hidden="true" />
          <p className="text-sm">{t("work.home.tasksAllClear")}</p>
        </Card>
      ) : (
        <ul
          aria-label={t("work.home.tasksLabel")}
          className="divide-y overflow-hidden rounded-xl border bg-card shadow-card"
        >
          {rows.map((row) => (
            <li
              key={row.key}
              className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 sm:flex-nowrap"
            >
              <span
                className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground [&_svg]:size-4"
                aria-hidden="true"
              >
                {row.icon}
              </span>
              <div className="min-w-0 flex-1">
                <p className="flex items-center gap-2 font-medium">
                  <span className={`size-2 shrink-0 rounded-full ${TONE_DOT[row.tone]}`} aria-hidden="true" />
                  {row.title}
                </p>
                {row.detail ? (
                  <div className="mt-0.5 flex min-w-0 flex-col gap-0.5 text-sm text-muted-foreground">
                    {row.detail}
                  </div>
                ) : null}
              </div>
              <Link
                to={row.action.to}
                search={row.action.search as never}
                className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-md border border-input bg-card px-3 text-sm font-medium hover:bg-muted"
              >
                {row.action.label}
                <ArrowRight className="size-4" aria-hidden="true" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ── Bugün ─────────────────────────────────────────────────────────────────────
function Change({ label, value }: { label: string; value: string | null }): ReactElement {
  return (
    <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
      {label}
      <StatusBadge tone={changeTone(value)}>{formatPercentChange(value)}</StatusBadge>
    </span>
  );
}

function Today({
  today,
  live,
  stationId,
}: {
  today: NonNullable<ReportsHome["today"]>;
  live: ReturnType<typeof useLiveToday>["data"];
  stationId: string | null;
}): ReactElement {
  const { t } = useTranslation();
  const stations = live?.stations.filter((row) => !stationId || row.stationId === stationId) ?? [];
  return (
    <section aria-labelledby="home-today-title" className="grid gap-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 id="home-today-title" className="text-lg font-semibold">
          {t("home.today.title")}
        </h2>
        <StatusBadge tone={today.source === "LIVE" ? "success" : "neutral"}>
          {today.source === "LIVE" ? t("home.today.live") : t("home.today.summary")}
        </StatusBadge>
      </div>
      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="grid content-start gap-2 p-5">
          <span className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <TrendingUp className="size-4" aria-hidden="true" />
            {t("home.today.revenue")}
          </span>
          <span className="tabular text-3xl font-semibold">{formatCompactMoney(today.current.revenue)}</span>
          <MoneyText value={today.current.revenue} className="text-sm text-muted-foreground" />
          <div className="flex flex-wrap gap-3">
            <Change label={t("home.today.vsYesterday")} value={today.revenueChangeVsYesterday} />
            <Change label={t("home.today.vsLastWeek")} value={today.revenueChangeVsLastWeek} />
          </div>
        </Card>
        <Card className="grid content-start gap-2 p-5">
          <span className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <Droplets className="size-4" aria-hidden="true" />
            {t("home.today.litres")}
          </span>
          <span className="text-3xl font-semibold">
            <QuantityText value={today.current.litres} unit="LITRE" />
          </span>
          <div className="flex flex-wrap gap-3">
            <Change label={t("home.today.vsYesterday")} value={today.litresChangeVsYesterday} />
            <Change label={t("home.today.vsLastWeek")} value={today.litresChangeVsLastWeek} />
          </div>
        </Card>
        <Card className="grid content-start gap-2 p-5">
          <h3 className="text-sm font-medium text-muted-foreground">{t("home.today.byUnit")}</h3>
          <ul className="grid gap-1.5 text-sm">
            {today.byUnit.map((row) => (
              <li key={row.unit} className="flex items-center justify-between gap-2">
                <span>{REPORT_SALES_UNIT_LABELS[row.unit]}</span>
                <span className="flex items-center gap-2">
                  <MoneyText value={row.revenue} />
                  <StatusBadge tone={changeTone(row.revenueChange)}>
                    {formatPercentChange(row.revenueChange)}
                  </StatusBadge>
                </span>
              </li>
            ))}
          </ul>
        </Card>
      </div>
      {stations.length > 1 || (stationId && stations.length === 1) ? (
        <Card className="overflow-x-auto p-0" tabIndex={0} role="group" aria-label={t("home.today.stations")}>
          <table className="w-full text-sm">
            <caption className="sr-only">{t("home.today.stations")}</caption>
            <thead className="bg-muted/50">
              <tr>
                <th scope="col" className="px-3 py-2 text-left font-medium">
                  {t("reports.columns.station")}
                </th>
                <th scope="col" className="px-3 py-2 text-right font-medium">
                  {t("home.today.revenue")}
                </th>
                <th scope="col" className="px-3 py-2 text-right font-medium">
                  {t("home.today.litres")}
                </th>
              </tr>
            </thead>
            <tbody>
              {stations.map((row) => (
                <tr key={row.stationId} className="border-t">
                  <td className="px-3 py-2">
                    {row.stationCode} · {row.stationName}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <MoneyText value={row.revenue} />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <QuantityText value={row.litres} unit="LITRE" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      ) : null}
      <p className="text-xs text-muted-foreground">{t("home.today.hint")}</p>
    </section>
  );
}

// ── Son 14 gün ────────────────────────────────────────────────────────────────
function Trend({ trend }: { trend: NonNullable<ReportsHome["trend"]> }): ReactElement {
  const { t } = useTranslation();
  return (
    <section aria-labelledby="home-trend-title" className="grid gap-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 id="home-trend-title" className="text-lg font-semibold">
          {t("home.trend.title")}
        </h2>
        <Link to="/raporlar" className={`text-sm ${linkClass}`}>
          {t("home.trend.allReports")}
        </Link>
      </div>
      <Card className="p-4">
        <ChartWithTable
          ariaLabel={t("home.trend.aria")}
          option={trendChartOption(trend, {
            revenue: t("home.trend.revenue"),
            litres: t("home.trend.litres"),
            revenueAxis: "₺",
            litresAxis: "L",
          })}
          height={300}
          tableLabel={t("home.trend.table")}
        >
          <table className="w-full text-sm">
            <caption className="sr-only">{t("home.trend.table")}</caption>
            <thead className="bg-muted/50">
              <tr>
                <th scope="col" className="px-3 py-2 text-left font-medium">
                  {t("reports.columns.date")}
                </th>
                <th scope="col" className="px-3 py-2 text-right font-medium">
                  {t("home.trend.revenue")}
                </th>
                <th scope="col" className="px-3 py-2 text-right font-medium">
                  {t("home.trend.litres")}
                </th>
              </tr>
            </thead>
            <tbody>
              {trend.map((day) => (
                <tr key={day.date} className="border-t">
                  <td className="px-3 py-2">{formatDate(day.date)}</td>
                  <td className="px-3 py-2 text-right">
                    <MoneyText value={day.revenue} />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <QuantityText value={day.litres} unit="LITRE" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </ChartWithTable>
      </Card>
    </section>
  );
}
